// src/pages/Pricing/Price.jsx
import React, { useMemo, useState } from "react";

function formatKRW(n) {
    if (typeof n !== "number" || Number.isNaN(n)) return "-";
    return n.toLocaleString("ko-KR");
}

function truncateToHundreds(n) {
    if (typeof n !== "number" || Number.isNaN(n)) return n;
    return Math.floor(n / 100) * 100;
}

// PLANS
const PLANS = [
    {
        id: "basic-one",
        name: "베이직 단품 플랜",
        monthly: 5500,
        isOneTime: true,
        accent: "#4B7BEC",
        features: [
            "AI 토큰 10개(기간 제한 없음)",
            "AI 리포트 제공",
            "AI 사진 배경 제거, 배경 변경"
        ],
    },
    {
        id: "standard",
        name: "스탠다드 플랜",
        monthly: 9900,
        isOneTime: false,
        accent: "#10B981",
        discountRate: 0.8,          // ✅ 연간 20% 할인
        features: [
            "AI 토큰 월 20개",
            "AI 리포트 제공",
            "AI 사진 배경 제거, 배경 변경",
            "연간 플랜 구매 시 20% 할인"
        ],
    },
    {
        id: "premium",
        name: "컨시어지 플랜",
        monthly: 25000,
        isOneTime: false,
        accent: "#F59E0B",
        discountRate: 0.7,          // ✅ 연간 30% 할인
        features: [
            "월 20회 소셜플랫폼 자동 홍보",
            "앱 토큰 10개 지급",
            "AI 리포트 제공",
            "AI 사진 배경 제거, 배경 변경, 이미지 필터 제공"
        ],
    },
];


export default function Price() {
    const [cycle, setCycle] = useState("monthly"); // 'monthly' | 'yearly'

    // computedPlans
    const computedPlans = useMemo(() => {
        return PLANS.map((p) => {
            if (p.isOneTime) {
                return { ...p, displayPrice: p.monthly, note: "(부가세 포함)", badge: null };
            }
            if (cycle === "monthly") {
                return { ...p, displayPrice: p.monthly, note: "(자동 연장, 부가세 포함)", badge: null };
            }

            if (p.id === "premium") {
                return { ...p, displayPrice: 300000, note: "(연간, 부가세 포함)", badge: null };
            }

            // ✅ 연간: 플랜별 할인율 사용 (기본 0.7 fallback)
            const rate = typeof p.discountRate === "number" ? p.discountRate : 0.7;
            const pct = Math.round((1 - rate) * 100); // 20, 30 ...
            const yearlyTotal = truncateToHundreds(Math.round(p.monthly * 12 * rate));
            return {
                ...p,
                displayPrice: yearlyTotal,
                note: `(연 ${pct}% 할인, 부가세 포함)`, // ✅ 문구도 플랜별 할인율 반영
                badge: `최대 ${pct}% 할인`,
            };
        });
    }, [cycle]);


    return (
        <section
            className="w-full"
            style={{ background: "#F5F6F9" }}
        >
            <div className="mx-auto max-w-[1100px] px-6 py-16">
                {/* 헤더 */}
                <div className="text-center mb-8">
                    <p className="text-sm text-gray-500">Prices</p>
                    <h2 className="text-3xl md:text-4xl font-extrabold mt-2 text-black">상품 및 가격</h2>
                    <p className="text-gray-500 py-3">
                        플랜 구성과 가격을 한번에 확인하고 wizMarket을 체험해보세요
                    </p>

                    {/* 토글 + 배지 (배지를 연간 버튼 위에 띄움) */}
                    {/* 헤더 - 토글 + 배지 */}
                    <div className="mt-6 inline-flex flex-col items-center">
                        {/* 헤더 - 토글 (배지는 연간 버튼 위에만) */}
                        <div className="mt-6 inline-flex items-center rounded-full bg-white shadow-sm p-1">
                            <button
                                type="button"
                                onClick={() => setCycle("monthly")}
                                className={`px-4 py-2 text-sm rounded-full transition ${cycle === "monthly" ? "bg-[#6F63F6] text-white" : "text-gray-700"
                                    }`}
                            >
                                월간 플랜
                            </button>

                            {/* 연간 버튼을 relative 래퍼로 감싸고 배지를 absolute로 올림 */}
                            <div className="relative">
                                {/* 배지: 항상 노출, 연간 버튼 위 중앙 */}
                                <div
                                    className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-2 rounded-full
             text-[11px] bg-black/70 text-white pointer-events-none
             whitespace-nowrap inline-flex items-center"
                                >
                                    최대 30% 할인
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setCycle("yearly")}
                                    className={`px-4 py-2 text-sm rounded-full transition ${cycle === "yearly" ? "bg-[#6F63F6] text-white" : "text-gray-700"
                                        }`}
                                >
                                    연간 플랜
                                </button>
                            </div>
                        </div>

                    </div>

                </div>


                {/* 카드들 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {computedPlans.map((p) => (
                        <article
                            key={p.id}
                            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col items-center text-center"
                        >
                            {/* 상단: 플랜명(강조색) + 강조 점(플랜명 아래, 중앙정렬) */}
                            <div className="flex flex-col items-center">
                                <h3
                                    className="font-extrabold text-lg"
                                    style={{ color: p.accent }}
                                >
                                    {p.name}
                                </h3>
                                <div className="mt-2 flex gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.accent }} />
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.accent }} />
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.accent }} />
                                </div>
                            </div>

                            {/* 가격 */}
                            <div className="mt-5">
                                <div className="text-3xl font-extrabold tracking-tight text-black">
                                    {formatKRW(p.displayPrice)}원
                                </div>
                                <div className="text-xs text-gray-500 mt-1">{p.note}</div>
                            </div>

                            {/* 구분선 */}
                            <div className="my-6 h-px bg-gray-200 w-full" />

                            {/* 기능 목록 (좌측 정렬 유지가 가독성 좋아서 w-full + text-left) */}
                            <ul className="w-full flex-1 space-y-2 text-sm text-gray-700 text-left">
                                {p.features.map((f, i) => (
                                    <li key={i} className="flex gap-2">
                                        <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-gray-300" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </div>

            </div>
        </section>
    );
}
